import TableLayout, { TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { Environment } from 'dashboard/models/environment';
import {
  ProductFeature,
  ProductFeatureName,
  ProductFeatureStatus,
} from 'dashboard/models/organization';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';

interface APIFeatures {
  [features: string]: string;
}

interface ProductFeaturesByEnv {
  [environment: string]: ProductFeatureByEnv;
}

interface ProductFeatureByEnv {
  [feature: string]: string;
}

interface DashboardAPIAccessTableProps {
  currentEnvironment: Environment;
  productFeatures: ProductFeature[];
  isLoading: boolean;
}

const API_FEATURES: APIFeatures = {
  [ProductFeatureName.AccountsCreateAPI]: 'Accounts',
  [ProductFeatureName.CasesCreateAPI]: 'Cases',
  [ProductFeatureName.ReportsCreateAPI]: 'Reports',
  [ProductFeatureName.TransactionsCreateAPI]: 'Transactions',
  [ProductFeatureName.VerificationsCreateAPI]: 'Verifications',
  [ProductFeatureName.WorkflowsEnhanced]: 'Workflows',
};

export const computeProductFeaturesByEnv = (productFeatures: ProductFeature[]) => {
  return productFeatures
    .filter((feature) => {
      return feature.name in API_FEATURES;
    })
    .reduce((acc: ProductFeaturesByEnv, feature: ProductFeature) => {
      if (acc[feature['environment-type']]) {
        acc[feature['environment-type']][API_FEATURES[feature['name']]] = feature['status'];
      } else {
        acc[feature['environment-type']] = {
          [API_FEATURES[feature['name']]]: feature['status'],
        };
      }
      return acc;
    }, {});
};

const DashboardAPIAccessTable = ({
  currentEnvironment,
  productFeatures,
  isLoading,
}: DashboardAPIAccessTableProps) => {
  const productFeaturesByEnv: ProductFeaturesByEnv = computeProductFeaturesByEnv(productFeatures);

  const FeaturePermission = (produceFeature: ProductFeatureByEnv) => {
    return produceFeature.status === ProductFeatureStatus.Enabled ? (
      <Sticker
        data-marketing-id='api-product-permissions-full'
        variant='success'
        role='button'
        tabIndex={0}
      >
        Full
      </Sticker>
    ) : (
      <Sticker
        data-marketing-id='api-product-permissions-limited'
        variant='caution'
        role='button'
        tabIndex={0}
      >
        Limited
      </Sticker>
    );
  };

  return (
    <Card>
      <Card.Body>
        <Card.Title>API endpoint access</Card.Title>
        <TableLayout
          dataTest='api-keys__table'
          isLoading={isLoading}
          showHeader={true}
          isEmpty={productFeaturesByEnv[currentEnvironment.type] == null}
          nullState={undefined}
          headerTR={
            <>
              <TableTH>Accounts</TableTH>
              <TableTH>Cases</TableTH>
              <TableTH>Reports</TableTH>
              <TableTH>Transactions</TableTH>
              <TableTH>Verifications</TableTH>
              <TableTH>Workflows</TableTH>
            </>
          }
        >
          <>
            {Object.keys(productFeaturesByEnv)
              .filter((envType) => envType === currentEnvironment.type)
              .map((productFeature) => (
                <TableTR data-test={`api-keys__table__row--${productFeature}`} key={productFeature}>
                  <TableTD>
                    <FeaturePermission status={productFeaturesByEnv[productFeature]?.Accounts} />
                  </TableTD>
                  <TableTD>
                    <FeaturePermission status={productFeaturesByEnv[productFeature]?.Cases} />
                  </TableTD>
                  <TableTD>
                    <FeaturePermission status={productFeaturesByEnv[productFeature]?.Reports} />
                  </TableTD>
                  <TableTD>
                    <FeaturePermission
                      status={productFeaturesByEnv[productFeature]?.Transactions}
                    />
                  </TableTD>
                  <TableTD>
                    <FeaturePermission
                      status={productFeaturesByEnv[productFeature]?.Verifications}
                    />
                  </TableTD>
                  <TableTD>
                    <FeaturePermission status={productFeaturesByEnv[productFeature]?.Workflows} />
                  </TableTD>
                </TableTR>
              ))}
          </>
        </TableLayout>
      </Card.Body>
    </Card>
  );
};

export default DashboardAPIAccessTable;
